import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { getToken, clearToken, API_BASE } from '../../api/client';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import { IoOpenOutline, IoChevronDownOutline, IoChevronForwardOutline, IoNotificationsOutline } from 'react-icons/io5';
import verticalMenuData from './verticalMenuData';
import './Admin.css';

const ROUTE_LABELS = {
  dashboard: 'Dashboard',
  categories: 'Categories',
  products: 'Products',
  'needs-attention': 'Need attention',
  add: 'Add Product',
  edit: 'Edit Product',
  variations: 'Variations',
  orders: 'Orders',
  coupons: 'Coupons',
  availability: 'Blocked dates',
  sync: 'Drive sync',
  firebase: 'Firebase Auth',
  pages: 'Pages',
  blog: 'Blog',
  'home-gallery': 'Home gallery',
  staff: 'Staff',
  roles: 'Roles',
  activity: 'Activity log',
  account: 'Account',
  rental: 'Rental',
  outward: 'Outward',
  inward: 'Inward',
  laundry: 'Laundry',
  damaged: 'Damaged',
  kanban: 'Kanban',
  notifications: 'Notifications',
  settings: 'Settings',
  checkout: 'Checkout',
  seo: 'SEO',
};

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getToken();
  const [permissions, setPermissions] = useState([]);
  const [roleSlug, setRoleSlug] = useState('');
  const [meLoaded, setMeLoaded] = useState(false);
  const pathSegments = location.pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
  const breadcrumbs = pathSegments.map((segment, i) => {
    const href = '/admin/' + pathSegments.slice(0, i + 1).join('/');
    const label = ROUTE_LABELS[segment] || segment;
    return { label, href };
  });

  useEffect(() => {
    if (!token) return;
    const url = API_BASE + '/api/admin/me';
    fetch(url, { headers: { Authorization: 'Bearer ' + token } })
      .then((res) => {
        if (res.status === 401) {
          clearToken();
          navigate('/admin/login', { replace: true });
          return;
        }
        if (res.status === 404) {
          // Legacy backend without /me: show all menu items
          setRoleSlug('super_admin');
          setMeLoaded(true);
          return;
        }
        if (res.ok) return res.json();
        throw new Error('Request failed');
      })
      .then((data) => {
        if (data) {
          setPermissions(data.permissions || []);
          setRoleSlug(data.role_slug || '');
        }
        setMeLoaded(true);
      })
      .catch(() => {
        // Network or other error: don't kick out, allow navigation with empty permissions
        setPermissions([]);
        setMeLoaded(true);
      });
  }, [token, navigate]);

  const handleLogout = () => {
    clearToken();
    navigate('/admin/login', { replace: true });
  };

  const canSee = (permission) => {
    if (!permission) return true;
    if (roleSlug === 'super_admin') return true;
    return permissions.includes(permission);
  };
  const systemPaths = ['/admin/sync', '/admin/sync/csv', '/admin/firebase', '/admin/roles', '/admin/activity', '/admin/account'];
  const rentalPaths = ['/admin/rental', '/admin/notifications'];
  const isCatalogActive =
    location.pathname.startsWith('/admin/products') || location.pathname.startsWith('/admin/variations');
  const isSystemActive = systemPaths.some((p) => location.pathname === p || (p !== '/admin/sync' && location.pathname.startsWith(p)));
  const isRentalActive = rentalPaths.some((p) => location.pathname === p || (p === '/admin/rental' && location.pathname.startsWith(p)));
  const [groupOpen, setGroupOpen] = useState(() => ({
    System: isSystemActive,
    'Rental Management': isRentalActive,
    Catalog: isCatalogActive,
  }));
  useEffect(() => {
    setGroupOpen((prev) => ({
      ...prev,
      ...(isCatalogActive && { Catalog: true }),
      ...(isSystemActive && { System: true }),
      ...(isRentalActive && { 'Rental Management': true }),
    }));
  }, [isCatalogActive, isSystemActive, isRentalActive]);
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    if (!token) return;
    const url = API_BASE + '/api/admin/notifications/unread-count';
    fetch(url, { headers: { Authorization: 'Bearer ' + token } })
      .then((r) => r.ok ? r.json() : { count: 0 })
      .then((d) => setUnreadCount(d.count || 0))
      .catch(() => {});
  }, [token, location.pathname]);
  const menuItems = verticalMenuData
    .map((item) => {
      if (item.group && item.children) {
        const visibleChildren = item.children.filter((c) => canSee(c.permission));
        return visibleChildren.length ? { ...item, children: visibleChildren } : null;
      }
      return canSee(item.permission) ? item : null;
    })
    .filter(Boolean);

  if (!token) {
    return null;
  }

  if (!meLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Typography color="text.secondary">Loading admin…</Typography>
      </Box>
    );
  }

  return (
    <div className="admin-layout">
      <nav className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <img
            src={`${process.env.PUBLIC_URL || ''}/gemwhite.png`}
            alt="Gem Fashion Studio Rental Boutique"
          />
        </div>
        {menuItems.map((item) => {
          if (item.group && item.children) {
            const Icon = item.icon;
            const isOpen = groupOpen[item.label] ?? false;
            return (
              <div key={item.label} className="admin-sidebar-group">
                <button
                  type="button"
                  className={`admin-sidebar-group-header ${isOpen ? 'open' : ''}`}
                  onClick={() => setGroupOpen((prev) => ({ ...prev, [item.label]: !prev[item.label] }))}
                  aria-expanded={isOpen}
                >
                  {Icon && <Icon size={20} />}
                  <span>{item.label}</span>
                  {isOpen ? <IoChevronDownOutline size={18} /> : <IoChevronForwardOutline size={18} />}
                </button>
                {isOpen && (
                  <div className="admin-sidebar-group-children">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink
                          key={child.href}
                          to={child.href}
                          end={child.end === true}
                          className={({ isActive }) => (isActive ? 'active' : '')}
                        >
                          {ChildIcon && <ChildIcon size={20} />}
                          <span>{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const Icon = item.icon;
          return (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              {Icon && <Icon size={20} />}
              <span>{item.label}</span>
            </NavLink>
          );
        })}
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="admin-sidebar-view-website"
        >
          <IoOpenOutline size={20} />
          <span>View website</span>
        </a>
      </nav>
      <Box className="admin-main" component="main">
        <div className="admin-header">
          <Box>
            <Breadcrumbs sx={{ mb: 0.5 }}>
              <Link component={NavLink} to="/admin/dashboard" underline="hover" color="inherit" sx={{ fontSize: '0.875rem' }}>
                Admin
              </Link>
              {breadcrumbs.map((b, i) =>
                i < breadcrumbs.length - 1 ? (
                  <Link key={b.href} component={NavLink} to={b.href} underline="hover" color="inherit" sx={{ fontSize: '0.875rem' }}>{b.label}</Link>
                ) : (
                  <Typography key={b.href} color="text.primary" sx={{ fontSize: '0.875rem' }}>{b.label}</Typography>
                )
              )}
            </Breadcrumbs>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Admin
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton component={NavLink} to="/admin/notifications" size="small" aria-label="Notifications">
              <Badge badgeContent={unreadCount} color="error">
                <IoNotificationsOutline size={22} />
              </Badge>
            </IconButton>
            <Button
              variant="outlined"
              size="small"
              onClick={handleLogout}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              textTransform: 'none',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
            >
              Logout
            </Button>
          </Box>
        </div>
        <Outlet />
      </Box>
    </div>
  );
}
